import { addDays, parseISO, format } from 'date-fns';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type ReactElement,
} from 'react';
import {
  findNextSlot,
  getAvailableProvidersForRFV,
  type GroupedCachedSlotsByLocation,
  type NearestSlot,
} from '../components/availability/availability.model';
import { DecisionTreeContext } from './DecisionTreeContext';

const RFV_MISMATCH_SCENE = 'reason-for-visit-provider-error' as const;

export type NoAvailabilityScenarioType =
  | 'date'
  | 'location'
  | 'other-providers'
  | 'no-providers';

export interface NoAvailabilityScenarioResult {
  type: NoAvailabilityScenarioType;
  nearestSlot?: NearestSlot;
}

export interface NoAvailabilityScenarioInputs {
  reasonForVisit: string;
  providerId: string;
  departmentId: string;
  slotDateString: string | null;
  cachedSlots: GroupedCachedSlotsByLocation | null;
}

export const computeNoAvailabilityScenario = (
  inputs: Partial<NoAvailabilityScenarioInputs> | null,
  rfvProvidersCount: number | null,
): NoAvailabilityScenarioResult | undefined => {
  if (!inputs?.reasonForVisit || !inputs.providerId || !inputs.slotDateString) {
    return undefined;
  }

  const { cachedSlots, departmentId, slotDateString } = inputs;

  if (cachedSlots) {
    // Current location lookup should only include future dates.
    const nextDay = format(addDays(parseISO(slotDateString), 1), 'yyyy-MM-dd');
    const nextAtLocation = findNextSlot(cachedSlots, nextDay, {
      onlyLocationId: departmentId,
    });
    if (nextAtLocation) {
      return { type: 'date', nearestSlot: nextAtLocation };
    }

    const atOtherLocation = findNextSlot(cachedSlots, slotDateString, {
      excludeLocationId: departmentId,
    });
    if (atOtherLocation) {
      return { type: 'location', nearestSlot: atOtherLocation };
    }
  }

  // We use <= 1 to account for the current provider
  if (rfvProvidersCount !== null && rfvProvidersCount <= 1) {
    return { type: 'no-providers' };
  }

  return { type: 'other-providers' };
};

interface NoAvailabilityScenarioContextValue {
  rfvProvidersCount: number | null;
  isLoading: boolean;
  setRfvProvidersCount: (count: number | null) => void;
  fetchRfvProvidersCount: (reasonForVisit: string) => Promise<void>;
  openNoRfvProvidersModal: (reasonForVisit: string) => void;
}

/* eslint-disable @typescript-eslint/no-empty-function */
const defaultValue: NoAvailabilityScenarioContextValue = {
  rfvProvidersCount: null,
  isLoading: false,
  setRfvProvidersCount: () => {},
  fetchRfvProvidersCount: async () => {},
  openNoRfvProvidersModal: () => {},
};
/* eslint-enable @typescript-eslint/no-empty-function */

const NoAvailabilityScenarioContext =
  createContext<NoAvailabilityScenarioContextValue>(defaultValue);

interface NoAvailabilityScenarioProviderProps {
  children: ReactNode;
}

export const NoAvailabilityScenarioProvider = ({
  children,
}: NoAvailabilityScenarioProviderProps): ReactElement => {
  const [rfvProvidersCount, setRfvProvidersCount] = useState<number | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(defaultValue.isLoading);
  const openedRfvMismatchModalRef = useRef<string | null>(null);
  const decisionTree = useContext(DecisionTreeContext);

  const fetchRfvProvidersCount = useCallback(
    async (reasonForVisit: string): Promise<void> => {
      if (!reasonForVisit) return;
      setIsLoading(true);
      try {
        const { availableProviders } =
          await getAvailableProvidersForRFV(reasonForVisit);
        setRfvProvidersCount(availableProviders.length);
      } catch {
        setRfvProvidersCount(null);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const openNoRfvProvidersModal = useCallback(
    (reasonForVisit: string) => {
      if (
        !reasonForVisit ||
        openedRfvMismatchModalRef.current === reasonForVisit
      )
        return;
      if (!decisionTree) return;

      openedRfvMismatchModalRef.current = reasonForVisit;
      decisionTree.updateDecisionTree({ rfvId: reasonForVisit });
      decisionTree.navigateToScene(RFV_MISMATCH_SCENE);
      decisionTree.setIsOpen(true);
    },
    [decisionTree],
  );

  const value = useMemo(
    (): NoAvailabilityScenarioContextValue => ({
      rfvProvidersCount,
      isLoading,
      setRfvProvidersCount,
      fetchRfvProvidersCount,
      openNoRfvProvidersModal,
    }),
    [
      rfvProvidersCount,
      isLoading,
      fetchRfvProvidersCount,
      openNoRfvProvidersModal,
    ],
  );

  return (
    <NoAvailabilityScenarioContext.Provider value={value}>
      {children}
    </NoAvailabilityScenarioContext.Provider>
  );
};

export const useNoAvailabilityScenario =
  (): NoAvailabilityScenarioContextValue => {
    const context = useContext(NoAvailabilityScenarioContext);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context ?? defaultValue;
  };
